import { useState, useEffect, useCallback } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

/**
 * Hook to fetch dynamic menu configuration from the backend.
 * The menu is filtered server-side based on the business's subscription plan and entitlements.
 */
export const useMenuConfig = () => {
  const { activeBusiness } = useBusiness();
  const { isAuthenticated } = useAuth();
  const [menuConfig, setMenuConfig] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [planName, setPlanName] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMenuConfig = useCallback(async () => {
    if (!activeBusiness?.id || !isAuthenticated) {
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data } = await api.get('/business/menu');

      console.log('[useMenuConfig] Menu loaded:', {
        moduleCount: data.menu?.length || 0,
        planName: data.plan_name,
        userRole: data.user_role
      });

      setMenuConfig(data.menu || []);
      setEntitlements(data.entitlements_summary || null);
      setPlanName(data.plan_name || null);
      setUserRole(data.user_role || null);
    } catch (err) {
      console.error('[useMenuConfig] Error fetching menu:', err);
      setError(err.message);
      // Return empty menu on error - let the sidebar show a loading/error state
      setMenuConfig([]);
    } finally {
      setLoading(false);
    }
  }, [activeBusiness?.id, isAuthenticated]);

  // Fetch menu when business changes or on initial load
  useEffect(() => {
    fetchMenuConfig();
  }, [fetchMenuConfig]);

  // Expose refresh function for manual refetch
  const refreshMenu = useCallback(() => {
    return fetchMenuConfig();
  }, [fetchMenuConfig]);

  return {
    menu: menuConfig,
    entitlements,
    planName,
    userRole,
    loading,
    error,
    refreshMenu
  };
};

export default useMenuConfig;
